import {
  BorderBox,
  Box,
  Button,
  FlexBox,
  Heading,
  Rhythm,
  Stack,
  TextSmall,
} from '@trmediaab/zebra';
import { Html } from '@trmediaab/zebra-components';
import type { Campaign, Product } from '@trmediaab/zebra-types/trhub';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import DigitalProduct from '../Icons/DigitalProduct';
import PrintProduct from '../Icons/PrintProduct';
import ProductBulletList from '../ProductBulletList';
import parseProduct from '../utils/parseProduct';
import PaymentInterval from './PaymentInterval';
import Popular from './Popular';
import Price from './Price';

interface ProductPresentationRefObject {
  readonly descriptionElement: HTMLDivElement | null;
  readonly termsElement: HTMLSpanElement | null;
}

interface ProductPresentationProps {
  product: Product;
  campaigns: Campaign[];
  onProductSelected: (product: Product) => void;
  onCampaignSelected: (campaign: Campaign) => void;
}

const ProductPresentation = forwardRef<
  ProductPresentationRefObject,
  ProductPresentationProps
>(({ product, campaigns, onProductSelected, onCampaignSelected }, ref) => {
  const {
    headline,
    description,
    buttonText,
    terms,
    price,
    reducedPrice,
    campaign,
  } = parseProduct(product, campaigns);

  const descriptionRef =
    useRef<ProductPresentationRefObject['descriptionElement']>(null);
  const termsRef = useRef<ProductPresentationRefObject['termsElement']>(null);

  useImperativeHandle(ref, () => ({
    get descriptionElement() {
      return descriptionRef.current;
    },
    get termsElement() {
      return termsRef.current;
    },
  }));

  const onClick = useCallback(() => {
    if (campaign != null) {
      onCampaignSelected(campaign);
    } else {
      onProductSelected(product);
    }
  }, [campaign, onCampaignSelected, onProductSelected, product]);

  const buttonProps: React.ComponentProps<typeof Button> = {
    bc: 'offersPrimary',
    width: '100%',
    px: 0,
    onClick,
  };

  return (
    <>
      <Rhythm between="4" sx={{ textAlign: 'center' }} ref={descriptionRef}>
        {product.distributable ? (
          <PrintProduct width="170px" color="checkout.icon2" mx="auto" />
        ) : (
          <DigitalProduct width="115px" color="checkout.icon2" mx="auto" />
        )}
        <Heading.h2 variant="offersPageProduct">{headline}</Heading.h2>
        <Html between={2} variant="checkoutRegular" html={description} />
      </Rhythm>

      <Price price={price} reducedPrice={reducedPrice} center />

      <TextSmall
        display="block"
        ref={termsRef}
        fontWeight="normal"
        lineHeight="medium"
        textAlign="center"
        color="text.fadedDarker"
        mt="2"
      >
        {terms}
      </TextSmall>
      <Button {...buttonProps}>{buttonText}</Button>
    </>
  );
});

interface ProductBoxProps {
  product: Product;
  campaigns: Campaign[];
  popular: boolean;
  onProductSelected: (product: Product) => void;
  onCampaignSelected: (campaign: Campaign) => void;
}

export interface ProductBoxRefObject {
  readonly textEl1: ProductPresentationRefObject['descriptionElement'];
  readonly textEl2: ProductPresentationRefObject['termsElement'];
  readonly textEl3: ProductPresentationRefObject['descriptionElement'];
  readonly textEl4: ProductPresentationRefObject['termsElement'];
}

const ProductBox = forwardRef<ProductBoxRefObject, ProductBoxProps>(
  (
    { product, campaigns, popular, onProductSelected, onCampaignSelected },
    ref,
  ) => {
    const primaryProductRef = useRef<ProductPresentationRefObject>(null);
    const secondaryProductRef = useRef<ProductPresentationRefObject>(null);

    useImperativeHandle(ref, () => ({
      get textEl1() {
        return primaryProductRef.current?.descriptionElement ?? null;
      },
      get textEl2() {
        return primaryProductRef.current?.termsElement ?? null;
      },
      get textEl3() {
        return secondaryProductRef.current?.descriptionElement ?? null;
      },
      get textEl4() {
        return secondaryProductRef.current?.termsElement ?? null;
      },
    }));

    const { bullets, isGiftProduct, type } = product;

    const paymentInterval =
      type === 'subscription' ? product.paymentInterval : null;

    return (
      <FlexBox>
        <BorderBox
          display="flex"
          flexDirection="column"
          boxShadow="offersPage.productBox"
          border="1"
          borderColor="offersPage.productBox.border"
          width="100%"
          css={`
            position: relative;
          `}
        >
          {isGiftProduct && paymentInterval != null ? (
            <PaymentInterval paymentInterval={paymentInterval} />
          ) : popular ? (
            <Popular />
          ) : null}
          <FlexBox
            bg="offers.backgroundPrimary"
            flexDirection="column"
            width="100%"
          >
            <Stack
              between="3"
              pt="4,5"
              pb="4,5"
              maxWidth="460px"
              width="100%"
              mx="auto"
              px={[3, null, '3,5']}
              flex="1"
            >
              <ProductPresentation
                ref={primaryProductRef}
                product={product}
                campaigns={campaigns}
                onProductSelected={onProductSelected}
                onCampaignSelected={onCampaignSelected}
              />
            </Stack>
          </FlexBox>
          <Box
            bg="offers.backgroundComplementary"
            px={[3, null, '3,5']}
            pt="3,5"
            pb="4"
            flex="1"
          >
            <Heading.h4 variant="offersPageBullets" pb="3" textAlign="center">
              Detta ingår
            </Heading.h4>
            <FlexBox justifyContent="center" mx="auto" maxWidth="380px">
              <ProductBulletList items={bullets} iconColor="checkout.icon2" />
            </FlexBox>
          </Box>
        </BorderBox>
      </FlexBox>
    );
  },
);

export default ProductBox;
